import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { VideoPlayer, mapCamera } from './MyCamerasPage';
import { NAV_LINKS, LOCAL_MODE_LINK } from '../components/Sidebar';
import { useServerUnreachable } from '../utils/serverReachability';
import { useEntitlements, canAccessMenu, productHas } from '../utils/entitlements';
import { logout } from '../store/slices/authSlice';
import { useTheme } from '../contexts/ThemeContext';
import { useSize } from '../contexts/SizeContext';
import ConfirmDialog from '../components/ConfirmDialog';
import Logo from '../components/Logo';
import { apiFetch, getToken, resetSession } from '../utils/streamApi';
import { rememberSos, stopActiveSos } from '../utils/sosState';
import { getAlertPollMs } from '../utils/alertPollConfig';
import { fetchUnread } from '../utils/unreadAlerts';
import { requestPushPermission, pushSupported, isPWA, unregisterPushOnLogout } from '../utils/pushNotifications';
import { PlayDevicesSection, PauseDevicesSection } from '../components/DevicesPanel';
import DevicesPausedBanner from '../components/DevicesPausedBanner';
import { DEVICES_PAUSE_CHANGED } from '../utils/devicesPause';
import '../css/cameras.css';
import '../css/parvekshak.css';
import '../css/navbar.css';
import '../css/dashboard.css';


// ═══════════════════════════════════════════════════════════════════════════
// Top half — swipeable live camera feed
// ═══════════════════════════════════════════════════════════════════════════
function CameraSwiper({ cameras, streamToken }) {
  const [idx, setIdx] = useState(0);
  const touchX = useRef(null);

  useEffect(() => { if (idx >= cameras.length) setIdx(0); }, [cameras.length, idx]);

  if (cameras.length === 0) {
    return <div className="dash-video-empty">No cameras configured</div>;
  }

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0 && idx < cameras.length - 1) setIdx(idx + 1);
    if (dx > 0 && idx > 0) setIdx(idx - 1);
  };

  const cam = cameras[idx];
  return (
    <div className="dash-video-wrap" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* "low" priority: a casual glance-through on the dashboard swiper never
          gets to evict a viewer who deliberately opened a camera to inspect it
          (wall focused tile / true fullscreen) — see hls_live_hq_start. */}
      <VideoPlayer camera={cam} streamToken={streamToken} isLarge hdPriority="low" />
      <div className="dash-cam-name">{cam.name}</div>
      {cameras.length > 1 && (
        <>
          {idx > 0 && (
            <button className="dash-nav-btn dash-nav-prev" onClick={() => setIdx(idx - 1)}>‹</button>
          )}
          {idx < cameras.length - 1 && (
            <button className="dash-nav-btn dash-nav-next" onClick={() => setIdx(idx + 1)}>›</button>
          )}
          <div className="dash-dots">
            {cameras.map((c, i) => (
              <span key={c.id} className={`dash-dot${i === idx ? ' active' : ''}`} onClick={() => setIdx(i)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Bottom sheets
// ═══════════════════════════════════════════════════════════════════════════
function DevicesSheet({ paused, remaining, onClose, onChanged, section }) {
  return (
    <div className="dash-sheet-overlay" onClick={onClose}>
      <div className="dash-sheet" onClick={e => e.stopPropagation()}>
        <div className="dash-sheet-handle" />
        <div className="dash-sheet-header">
          <h3>{section === 'pause' ? '⏸ Pause Devices' : '▶️ Play Devices'}</h3>
          <button className="dash-sheet-x" onClick={onClose}>✕</button>
        </div>
        {section === 'play' && <PlayDevicesSection />}
        {section === 'pause' && (
          <PauseDevicesSection paused={paused} remaining={remaining} onChanged={onChanged} onDone={onClose} />
        )}

        <button className="dash-sheet-close" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

function BrowserPushRow() {
  const [perm, setPerm] = useState(() =>
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const [busy, setBusy] = useState(false);

  if (!pushSupported()) return null;

  const isStandalone = isPWA();
  const label = perm === 'granted'   ? '✓ Browser notifications on'
              : perm === 'denied'    ? 'Notifications blocked in browser settings'
              : busy                 ? 'Requesting…'
              : 'Enable browser notifications';

  const handleEnable = async () => {
    if (perm === 'granted' || perm === 'denied') return;
    setBusy(true);
    const granted = await requestPushPermission();
    setPerm(granted ? 'granted' : Notification.permission);
    setBusy(false);
  };

  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
      <p className="dash-sheet-section-label" style={{ marginBottom: 8 }}>This browser / device</p>
      {!isStandalone && perm !== 'granted' && (
        <p className="dash-sheet-sub" style={{ marginBottom: 8 }}>
          On iPhone, add Parvekshak to your Home Screen first — Safari requires that for push notifications.
        </p>
      )}
      <button
        className="dash-sheet-close"
        style={{
          opacity: perm === 'denied' ? 0.5 : 1,
          cursor: perm === 'denied' ? 'default' : 'pointer',
          background: perm === 'granted' ? 'transparent' : undefined,
          color: perm === 'granted' ? 'var(--text-muted)' : undefined,
          border: perm === 'granted' ? '1px solid var(--border)' : undefined,
        }}
        disabled={perm === 'granted' || perm === 'denied' || busy}
        onClick={handleEnable}
      >
        {label}
      </button>
    </div>
  );
}

// For the customer this is ON or OFF. Nothing else.
//
// It used to carry its own mode (off / scheduled / 24x7) and its own start and end
// times, independent of the protection hours set during setup. Push defaults to
// 00:30-05:00, so somebody protected from 22:00 had a phone that stayed silent
// until half past midnight — and the fix was to notice two separate schedules
// existed and reconcile them by hand. A customer cannot be expected to do that,
// and does not think of "when am I protected" and "when does my phone ring" as
// two questions.
//
// So the times are gone. ON means the phone rings whenever the system is
// protecting; OFF is a mute, which is a real thing to want and is not a schedule.
// The Admin screens still expose the underlying settings separately.
function NotificationsSheet({ onClose }) {
  const [mode,    setMode]    = useState('scheduled');  // scheduled | always | off
  const [bellHelp, setBellHelp] = useState(false);      // Doorbell explainer (a tap, not a hover — phones)
  const [start,   setStart]   = useState('23:00');
  const [end,     setEnd]     = useState('06:00');
  const [saved,   setSaved]   = useState(false);
  const [err,     setErr]     = useState(false);
  const [cameras, setCameras] = useState([]);
  // Box is actually watching round the clock. A site that chose "I'm out" before
  // PRVSK-123 has the noise window at 24x7 but no away flag until Save is pressed —
  // deliberately not switched on by the upgrade, which could set sirens off for a
  // household that forgot it was still on.
  const [away,    setAway]    = useState(false);

  useEffect(() => {
    // The noise window — siren, lights and phone together. One setting, because a
    // notification is a siren in your pocket and wakes the house the same way.
    apiFetch('/settings/noise-window')
      .then(r => r.json())
      .then(d => {
        setMode(d.mode || 'scheduled');
        setAway(!!d.away);
        if (d.start) setStart(d.start);
        if (d.end)   setEnd(d.end);
      })
      .catch(() => {});
    apiFetch('/ui/cameras')
      .then(r => r.json())
      .then(d => setCameras(d.cameras || []))
      .catch(() => {});
  }, []);

  const toggleCamPush = async (cam) => {
    const current = cam.alert_actions?.push !== false;
    const updated = { ...cam, alert_actions: { ...(cam.alert_actions || {}), push: !current } };
    setCameras(cs => cs.map(c => c.idx === cam.idx ? updated : c));
    try {
      await apiFetch(`/cameras/${cam.idx}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alert_actions: updated.alert_actions }),
      });
    } catch {
      setCameras(cs => cs.map(c => c.idx === cam.idx ? cam : c));
    }
  };

  // Doorbell (PRVSK-139): every person on this camera reaches the phone with their
  // photo, at any hour — past the push switch above, the hours and "Off".
  // Off / names / instant. "names" waits a few seconds to recognise known people;
  // "instant" rings in 1–2 s for anyone (family may show as "Person").
  const setCamDoorbell = async (cam, mode) => {
    const doorbell = mode === 'off' ? false : mode;
    const updated = { ...cam, alert_actions: { ...(cam.alert_actions || {}), doorbell } };
    setCameras(cs => cs.map(c => c.idx === cam.idx ? updated : c));
    try {
      const r = await apiFetch(`/cameras/${cam.idx}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alert_actions: updated.alert_actions }),
      });
      if (!r.ok) throw new Error();
    } catch {
      setCameras(cs => cs.map(c => c.idx === cam.idx ? cam : c));
    }
  };

  // Writes the noise window, which the server applies to the siren, the lights and
  // the phone together. The stored watching hours are never edited, but "I'm out"
  // also switches the box to watch round the clock until Night is chosen again
  // (PRVSK-123) — an empty home has to be watched all day, not only be noisy.
  const save = async () => {
    try {
      const r = await apiFetch('/settings/noise-window', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, start, end }),
      });
      if (!r.ok) throw new Error();   // e.g. a cleared time — the box refuses it
      setAway(mode === 'always');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setErr(true);
      setTimeout(() => setErr(false), 2000);
    }
  };

  const desc = {
    always:    away
      ? 'Away: every camera looks for intruders day and night, and siren, lights and '
        + 'your phone can go off at any hour. Switch back to Night when you are home.'
      : 'Siren, lights and your phone can go off at any hour. Press Save to also have '
        + 'every camera look for intruders day and night while you are out.',
    off:       'Nothing will sound and your phone stays quiet. Cameras keep watching and '
             + 'recording, and an SOS always comes through.',
    scheduled: `Siren and lights are armed between ${start} and ${end}, and your phone is `
             + 'alerted at the same time. Outside those hours the cameras still watch '
             + 'and record — nothing makes a sound.',
  }[mode];

  return (
    <div className="dash-sheet-overlay" onClick={onClose}>
      <div className="dash-sheet" onClick={e => e.stopPropagation()}>
        <div className="dash-sheet-handle" />
        <div className="dash-sheet-header">
          <h3>🚨 When to scare them off</h3>
          <button className="dash-sheet-x" onClick={onClose}>✕</button>
        </div>

        <p className="dash-sheet-sub" style={{ marginBottom: 8 }}>
          When the siren and floodlights are allowed to go off — the part that actually
          turns an intruder around. Your phone is alerted at the same time.
        </p>
        {/* Three presets so "I'm going out today" is one tap and reverting is one
            tap. The alternative — editing two times every time you leave the house
            — is the kind of thing a customer does twice and then stops doing. */}
        <div className="dash-push-seg">
          {[['scheduled', 'Night'], ['always', "I'm out"], ['off', 'Off']].map(([val, label]) => (
            <button key={val}
              className={`dash-push-seg-btn${mode === val ? ' active' : ''}`}
              onClick={() => setMode(val)}>
              {label}
            </button>
          ))}
        </div>

        {mode === 'scheduled' && (
          <>
            <div className="dash-notif-row">
              <span className="dash-notif-label">From</span>
              <input type="time" className="dash-notif-time" value={start}
                     onChange={e => setStart(e.target.value)} />
            </div>
            <div className="dash-notif-row">
              <span className="dash-notif-label">Until</span>
              <input type="time" className="dash-notif-time" value={end}
                     onChange={e => setEnd(e.target.value)} />
            </div>
          </>
        )}

        <p className="dash-sheet-sub" style={{ marginTop: 10 }}>{desc}</p>

        <BrowserPushRow />

        <button className="dash-sheet-close" style={{ marginTop: 8 }} onClick={save}>
          {saved ? '✓ Saved' : err ? 'Save failed' : 'Save'}
        </button>

        {cameras.length > 0 && (
          <>
            <p className="dash-sheet-section-label" style={{ marginTop: 20, marginBottom: 8 }}>Per-Camera Push</p>
            <p className="dash-sheet-sub" style={{ marginTop: 0, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>🔔 Doorbell: anyone at this camera sends a notification with their photo, at any hour.</span>
              <button type="button" onClick={() => setBellHelp(v => !v)} aria-expanded={bellHelp}
                      aria-label="What does Doorbell do?"
                      style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', border: '1.5px solid var(--border)',
                               background: bellHelp ? 'var(--primary)' : 'var(--surface-alt)',
                               color: bellHelp ? 'var(--btn-primary-text, #000)' : 'var(--text-muted)',
                               fontSize: 12, fontWeight: 700, lineHeight: '18px', padding: 0, cursor: 'pointer' }}>
                i
              </button>
            </p>
            {bellHelp && (
              // PRVSK-139: what the switch overrides and what it leaves alone, in the
              // customer's words — the hours above are about the siren, this isn't.
              <div role="note" style={{ margin: '0 0 10px', padding: '10px 12px', borderRadius: 10, fontSize: 13,
                                        lineHeight: 1.45, background: 'var(--surface-alt)', border: '1px solid var(--border)',
                                        color: 'var(--text)' }}>
                <b>Like a doorbell for this camera.</b> When someone is seen here, your phone gets a
                notification with their photo — <b>at any hour</b>, even outside the hours above, when
                this camera's push is Off, or when Siren hours is set to Off.
                <br />The siren and lights still follow the hours above.
                <br /><b>With names</b> waits a few seconds to see the face
                {productHas('known-faces-add') ? ', so family you\'ve added as known people ring by name' : ''}.
                <br /><b>Instant</b> rings in 1–2 seconds for anyone — someone you know may first show
                as "Person".
              </div>
            )}
            {cameras.map(cam => {
              const enabled = cam.alert_actions?.push !== false;
              const bellV   = cam.alert_actions?.doorbell;
              const bell    = bellV === 'instant' ? 'instant' : (bellV === true || bellV === 'names') ? 'names' : 'off';
              return (
                <div key={cam.idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 14, color: 'var(--text)' }}>{cam.name}</span>
                  <span style={{ display: 'flex', gap: 8 }}>
                  {/* One control, three states — no second switch on a narrow phone row. */}
                  <select
                    className="dash-bell-select"
                    value={bell}
                    onChange={e => setCamDoorbell(cam, e.target.value)}
                    aria-label={`Doorbell for ${cam.name}`}
                    title="Doorbell: every person here sends a notification with their photo, at any hour"
                    style={{
                      padding: '5px 10px', borderRadius: 20, fontWeight: 700, cursor: 'pointer',
                      background: bell !== 'off' ? 'var(--primary)' : 'var(--surface-alt)',
                      color: bell !== 'off' ? 'var(--btn-primary-text, #000)' : 'var(--text-muted)',
                      border: bell !== 'off' ? '1.5px solid transparent' : '1.5px solid var(--border)',
                    }}>
                    <option value="off">🔔 Off</option>
                    <option value="names">🔔 With names</option>
                    <option value="instant">🔔 Instant</option>
                  </select>
                  <button
                    onClick={() => toggleCamPush(cam)}
                    style={{
                      padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                      border: 'none', cursor: 'pointer',
                      background: enabled ? 'var(--primary)' : 'var(--surface-alt)',
                      color: enabled ? 'var(--btn-primary-text, #000)' : 'var(--text-muted)',
                      border: enabled ? 'none' : '1.5px solid var(--border)',
                    }}>
                    {enabled ? 'On' : 'Off'}
                  </button>
                  </span>
                </div>
              );
            })}
          </>
        )}

        <button className="dash-sheet-close" style={{ marginTop: 16, background: 'transparent', color: 'var(--text-muted)' }} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

// Topics / tips about features the product leaves out (CLIENT_TYPE) are dropped.
const HELP_TOPIC_MENU = { known: 'known-faces-add', ban: 'banned', recordings: 'recordings' };
function helpTopics() {
  return HELP_TOPICS
    .filter(t => !HELP_TOPIC_MENU[t.key] || productHas(HELP_TOPIC_MENU[t.key]))
    .map(t => ({ ...t, body: (t.body || []).filter(sec =>
      productHas('known-faces-add') || !(sec.steps || []).some(st => st.includes('Add to Known'))) }));
}

const HELP_TOPICS = [
  {
    key: 'noalerts', icon: '🔕', label: 'Not getting alerts',
    body: [
      { heading: 'Check the alert schedule', steps: ['Open Dashboard → tap Schedule', 'Make sure the global schedule is set to Always On or covers the current time', 'Check if any device schedule is overriding the global one'] },
      { heading: 'Check devices are not paused', steps: ['Open Dashboard → tap Devices', 'If a pause is active, tap Resume Now'] },
      { heading: 'Check phone notifications', steps: ['Make sure your phone allows notifications for this app', 'Check that Do Not Disturb / Focus mode is off'] },
    ],
  },
  {
    key: 'camera', icon: '📷', label: 'Camera offline / no stream',
    body: [
      { heading: 'Quick fixes', steps: ['Check the camera is powered on and connected to the network', 'Open Admin Config → Cameras → tap ↻ Refresh to reload the camera list', 'Tap Restart Cameras to soft-restart all connected cameras'] },
      { heading: 'If still offline', steps: ['Unplug the camera for 10 seconds and plug it back in', 'Check that the camera IP has not changed (some routers reassign IPs after a restart)', 'If using an NVR — check the NVR is online first'] },
    ],
  },
  {
    key: 'false', icon: '🚨', label: 'Getting false alerts',
    body: [
      { heading: 'Mark the alert as invalid', steps: ['Open Alerts → find the alert', 'Tap ✕ Mark Invalid — removes it from the review queue'] },
      { heading: 'Person keeps triggering alerts', steps: ['Open Alerts → tap ➕ Add to Known on the alert photo', 'Give the person a name and save — they will be recognised and ignored from now on'] },
      { heading: 'Limit alert hours', steps: ['Open Dashboard → Schedule', 'Set a time window so alerts only fire when relevant (e.g. 10 PM – 6 AM)'] },
    ],
  },
  {
    key: 'schedule', icon: '⏰', label: 'Alert schedule',
    body: [
      { heading: 'How it works', steps: ['The alert schedule controls when the system fires alerts', 'Outside the scheduled window, no alerts are sent even if motion is detected', 'Banned persons are always alerted regardless of the schedule'] },
      { heading: 'Change the schedule', steps: ['Open Dashboard → tap Schedule', 'Choose Always On, Off, or Time Window for the global schedule', 'Each relay device can also have its own schedule override'] },
      { heading: 'Tip', steps: ['If alerts suddenly stop at a certain hour, check whether the schedule end time has been reached'] },
    ],
  },
  {
    key: 'known', icon: '👤', label: 'Adding a known person',
    body: [
      { heading: 'From an alert', steps: ['Open Alerts → tap ➕ Add to Known on an alert photo', 'Enter a name and save — recognition is active immediately'] },
      { heading: 'From the menu', steps: ['Open the hamburger menu → Known Faces', 'Tap Add Person and follow the steps'] },
      { heading: 'Tips for best accuracy', steps: ['Add 2–3 photos in different lighting conditions', 'Use both Day and Night profile photos if the camera runs at night'] },
    ],
  },
  {
    key: 'ban', icon: '🚫', label: 'Ban / Unban a person',
    body: [
      { heading: 'To ban', steps: ['Open Alerts → tap 🚫 Add to Banned on an alert photo', 'That person will trigger high-priority alerts on all cameras immediately'] },
      { heading: 'To unban', steps: ['Open the hamburger menu → Banned Faces', 'Select the person and tap Remove'] },
      { heading: 'Note', steps: ['Banned persons trigger alerts at any time regardless of the alert schedule'] },
    ],
  },
  {
    key: 'recordings', icon: '📼', label: 'Viewing recordings',
    body: [
      { heading: 'How to access', steps: ['Tap the 📼 Recordings tile on the Dashboard', 'Or open the hamburger menu → Recordings', 'Filter by camera and date range to find a specific clip'] },
      { heading: 'If recordings are missing', steps: ['Check that recording is enabled in Admin Config → Cameras for that camera', 'Check available storage in Admin Config → Health → NVR Storage'] },
    ],
  },
  {
    key: 'devices', icon: '🔊', label: 'Sirens, lights & speakers',
    body: [
      { heading: 'Test a device', steps: ['Open Dashboard → tap Play Devices (on a computer: Play & Pause Devices)', 'Switch a speaker or relay on, then off'] },
      { heading: 'Pause devices', steps: ['Open Dashboard → tap Pause Devices (on a computer: Play & Pause Devices)', 'Choose a duration — relays and speakers will not trigger during the pause', 'Alerts still fire normally while paused'] },
      { heading: 'Manage devices', steps: ['Open Admin Config → Manage Devices to add, rename, or configure relay channels and audio devices'] },
    ],
  },
];

function HelpTopicDetail({ topic, onBack }) {
  return (
    <div className="dash-help-detail">
      <div className="dash-help-detail-nav">
        <button className="dash-help-back" onClick={onBack}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M12 4L6 10L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Help
        </button>
      </div>
      <div className="dash-help-detail-hero">
        <span className="dash-help-hero-icon">{topic.icon}</span>
        <h2 className="dash-help-hero-title">{topic.label}</h2>
      </div>
      <div className="dash-help-body">
        {topic.body.map((section, i) => (
          <div key={i} className="dash-help-section">
            {section.heading && (
              <div className="dash-help-section-hdr">{section.heading}</div>
            )}
            <div className="dash-help-steps">
              {section.steps.map((step, j) => (
                <div key={j} className="dash-help-step">
                  <span className="dash-help-step-num">{j + 1}</span>
                  <span className="dash-help-step-text">{step}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HelpSheet({ onClose }) {
  const [topic, setTopic] = useState(null);
  return (
    <div className="dash-sheet-overlay" onClick={onClose}>
      <div className="dash-sheet dash-help-sheet" onClick={e => e.stopPropagation()}>
        <div className="dash-sheet-handle" />
        {topic ? (
          <HelpTopicDetail topic={topic} onBack={() => setTopic(null)} />
        ) : (
          <>
            <div className="dash-sheet-header">
              <h3>❓ Help</h3>
              <button className="dash-sheet-x" onClick={onClose}>✕</button>
            </div>
            <div className="dash-help-topics">
              {helpTopics().map(t => (
                <button key={t.key} className="dash-help-topic-btn" onClick={() => setTopic(t)}>
                  <span className="dash-help-topic-icon">{t.icon}</span>
                  <span className="dash-help-topic-label">{t.label}</span>
                  <span className="dash-help-topic-arrow">›</span>
                </button>
              ))}
            </div>
            <a
              className="dash-help-email-btn"
              href="mailto:contact@parvekshak.com"
            >
              <span className="dash-help-email-icon">✉️</span>
              <div className="dash-help-email-text">
                <span className="dash-help-email-label">Still need help?</span>
                <span className="dash-help-email-addr">contact@parvekshak.com</span>
              </div>
              <span className="dash-help-topic-arrow">›</span>
            </a>
          </>
        )}
        <button className="dash-sheet-close" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Hamburger drawer — access to all other pages (Alerts, Admin Config, etc.)
// ═══════════════════════════════════════════════════════════════════════════
function NavDrawer({ onClose, onLogout }) {
  const navigate = useNavigate();
  const entitlements = useEntitlements();
  // Mirror Sidebar.jsx's gating — otherwise industrial-only links (and any
  // menu the role lacks) leak into this drawer, which is the only nav surface
  // on mobile/PWA (desktop uses Sidebar directly).
  const unreachable = useServerUnreachable();
  // The Dashboard is phone-only, and desktopOnly links are what its tiles already do.
  const roleLinks = NAV_LINKS.filter(l => canAccessMenu(l, entitlements) && !l.desktopOnly);
  const visibleLinks = unreachable ? [LOCAL_MODE_LINK, ...roleLinks] : roleLinks;
  // '/' redirects back to /dashboard on mobile (by design) — use the
  // unguarded alias so "Cameras" actually opens MyCamerasPage from here.
  const go = (path) => { navigate(path === '/' ? '/cameras' : path); onClose(); };
  return (
    <div className="dash-drawer-overlay" onClick={onClose}>
      <div className="dash-drawer" onClick={e => e.stopPropagation()}>
        <div className="dash-drawer-header">
          <span style={{ display:'flex', alignItems:'center', gap:8 }}><Logo size={20} /> Menu</span>
          <button className="dash-drawer-close" onClick={onClose} aria-label="Close menu">✕</button>
        </div>
        <nav className="dash-drawer-nav">
          <button className="dash-drawer-link" onClick={() => go('/dashboard')}>
            <span className="dash-drawer-icon">🏠</span>
            <span>Dashboard</span>
          </button>
          {visibleLinks.map(l => (
            <div key={l.path} style={{ display: 'contents' }}>
              {l.section && <div className="dash-drawer-section">{l.section}</div>}
              <button className="dash-drawer-link" onClick={() => go(l.path)}>
                <span className="dash-drawer-icon">{l.icon}</span>
                <span>{l.label}{l.beta && <span className="dash-drawer-beta">β</span>}</span>
              </button>
            </div>
          ))}
        </nav>
        <button className="dash-drawer-link dash-drawer-logout" onClick={onLogout}>
          <span className="dash-drawer-icon">⏻</span>
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════════════════════════
export default function DashboardPage() {
  const navigate = useNavigate();
  const dispatch  = useDispatch();
  const { themeKey, themes, switchTheme } = useTheme();
  const { sizeKey, sizes, switchSize } = useSize();
  const isMobile = window.innerWidth <= 768;
  const [cameras,     setCameras]     = useState([]);
  const [streamToken, setStreamToken] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [sheet,        setSheet]        = useState(null); // 'play' | 'pause' | 'help' | null
  const [sosConfirm,   setSosConfirm]   = useState(false);
  const [sosReason,    setSosReason]    = useState('Suspicious Person');
  const [sosOtherText, setSosOtherText] = useState('');
  const [sosBusy,      setSosBusy]      = useState(false);
  const [sosDone,      setSosDone]      = useState(false);
  const [sosId,        setSosId]        = useState('');
  const [sosStopping,  setSosStopping]  = useState(false);
  const [sosStopped,   setSosStopped]   = useState(false);
  const [toast,        setToast]        = useState('');
  const [pauseState,   setPauseState]   = useState({ paused: false, remaining_secs: 0 });
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [drawerOpen,   setDrawerOpen]   = useState(false);
  // "I'm out" is on — shown on the Siren hours tile (PRVSK-123).
  const [away,         setAway]         = useState(false);
  // Siren hours = Off — also on the tile, so a party-night Off isn't forgotten (PRVSK-124).
  const [muted,        setMuted]        = useState(false);
  const loadAway = () => {
    apiFetch('/settings/noise-window')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) { setAway(!!d.away); setMuted(d.devices === 'muted'); } })
      .catch(() => {});
  };
  useEffect(loadAway, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => { unregisterPushOnLogout(); resetSession(); dispatch(logout()); navigate('/login', { replace: true }); };

  const loadPauseStatus = () => {
    apiFetch('/controls/devices/pause-status').then(r => r.json()).then(setPauseState).catch(() => {});
  };
  // A resume from the "Devices paused" bar updates the Pause Devices tile too.
  useEffect(() => {
    window.addEventListener(DEVICES_PAUSE_CHANGED, loadPauseStatus);
    return () => window.removeEventListener(DEVICES_PAUSE_CHANGED, loadPauseStatus);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      const token = await getToken();
      setStreamToken(token);
      try {
        const res = await apiFetch('/cameras');
        if (res.ok) {
          const data = await res.json();
          setCameras((data.cameras || []).map(cam => mapCamera(cam, cam.idx, data.device)));
        }
      } catch {}
      setLoading(false);
      loadPauseStatus();
    })();
  }, []);

  // keep the "Resume (Xh left)" label fresh while paused
  useEffect(() => {
    if (!pauseState.paused) return;
    const iv = setInterval(loadPauseStatus, 30000);
    return () => clearInterval(iv);
  }, [pauseState.paused]);

  // Unread alerts for this device (PRVSK-84). Opening the Dashboard no longer
  // marks anything as seen — only viewing the Alerts page does.
  useEffect(() => {
    const refresh = () => {
      fetchUnread().then(d => { if (d) setUnreadAlerts(d.count || 0); });
    };
    refresh();

    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    // The app calls this when it returns to the foreground.
    window.__onAppForeground = refresh;

    const ms = getAlertPollMs();
    const iv = ms ? setInterval(refresh, ms) : null;
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (window.__onAppForeground === refresh) window.__onAppForeground = null;
      if (iv) clearInterval(iv);
    };
  }, []);

  const showToast = (msg, ms = 2000) => { setToast(msg); setTimeout(() => setToast(''), ms); };

  const SOS_REASONS = [
    { label: '👤 Suspicious Person',    key: 'Suspicious Person'   },
    { label: '🚨 Break-in / Intrusion', key: 'Break-in / Intrusion'},
    { label: '🏥 Medical Emergency',    key: 'Medical Emergency'   },
    { label: '💬 Other',                key: 'Other'               },
  ];

  const fireSOS = async () => {
    if (!sosReason) return;
    const reason = sosReason === 'Other' ? (sosOtherText.trim() || 'Other') : sosReason;
    setSosBusy(true);
    try {
      let device_model = window.FLUTTER_DEVICE_MODEL || '';
      if (!device_model) {
        const m = navigator.userAgent.match(/;\s*([^;)]+)\s+Build\//i);
        if (m) device_model = m[1].trim();
      }
      const res = await apiFetch('/admin/panic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, device_model }),
      });
      // fetch doesn't throw on an HTTP error — without this a failed trigger
      // still showed "Panic Sent!".
      const data = res.ok ? await res.json().catch(() => ({})) : {};
      if (!data.sos_id) throw new Error('not sent');
      setSosId(data.sos_id);
      // Kept on the device (PRVSK-122) — the "SOS active" bar can stop it after
      // this window is closed or the page reloads.
      rememberSos(data.sos_id);
      setSosDone(true);
    } catch {
      showToast('Panic alert NOT sent — check the connection and try again', 5000);
      setSosConfirm(false);
      setSosReason('Suspicious Person');
      setSosOtherText('');
    } finally {
      setSosBusy(false);
    }
  };

  const closeSOS = () => {
    setSosConfirm(false);
    setSosDone(false);
    setSosStopped(false);
    setSosId('');
    setSosReason('Suspicious Person');
    setSosOtherText('');
  };

  const stopSOS = async () => {
    if (!sosId || sosStopping) return;
    setSosStopping(true);
    try {
      const res = await stopActiveSos();
      if (!res.ok) {
        showToast(res.message, 6000);
        return;
      }
      setSosStopped(true);
      setTimeout(closeSOS, 1500);
    } catch {
      showToast('Could not stop the SOS — check the connection and try again', 5000);
    } finally {
      setSosStopping(false);
    }
  };

  if (loading) {
    return (
      <div className="dash-root dash-loading">
        <div className="connecting-spinner" />
      </div>
    );
  }

  return (
    <div className="dash-root">
      <div className="dash-topbar">
        <button className="dash-more-btn" onClick={() => setDrawerOpen(true)} title="Menu" aria-label="Open menu">☰</button>
        <span className="dash-title"><Logo size={20} /> Parvekshak</span>
        <div style={{ flex: 1 }} />
        {!isMobile && (
          <select className="theme-select" value={sizeKey} onChange={e => switchSize(e.target.value)} title="UI layout size">
            {sizes.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        )}
        <select className="theme-select" value={themeKey} onChange={e => switchTheme(e.target.value)} title="Switch theme">
          {themes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>
      <DevicesPausedBanner />
      <div className="dash-video-half">
        <CameraSwiper cameras={cameras} streamToken={streamToken} />
      </div>

      <div className="dash-actions-half">
        <button className="dash-sos-btn" onClick={() => setSosConfirm(true)}>🆘 SOS / PANIC</button>

        <div className="dash-grid">
          {/* Row 1: Play Devices | Notifications | Alerts */}
          <button className="dash-grid-btn" onClick={() => setSheet('play')}>
            <span className="dash-grid-icon-circle dash-icon-blue">▶️</span>
            <span className="dash-grid-label">Play Devices</span>
          </button>
          <button className="dash-grid-btn dash-grid-btn--flagged" onClick={() => setSheet('notifications')}
                  title={away ? "I'm out — every camera is watching round the clock"
                       : muted ? 'Off — siren, lights and phone stay quiet. Tap to switch back on.' : undefined}>
            {/* Corner pill, out of the flow: a third line pushed the icon out of line
                with the other tiles. */}
            {away && <span className="dash-grid-flag" aria-label="Away, guarding 24 hours">Away · 24h</span>}
            {!away && muted && (
              <span className="dash-grid-flag dash-grid-flag--off" aria-label="Siren hours off, nothing will sound">🔕 Off</span>
            )}
            <span className="dash-grid-icon-circle dash-icon-purple">🔔</span>
            <span className="dash-grid-label">Siren hours</span>
          </button>
          <button className="dash-grid-btn" onClick={() => navigate('/alerts')}>
            <span className="dash-grid-icon-circle dash-icon-green" style={{ position: 'relative' }}>
              🚨
              {unreadAlerts > 0 && (
                <span style={{
                  position: 'absolute', top: -4, right: -4,
                  background: '#e53935', color: '#fff',
                  fontSize: 10, fontWeight: 700, lineHeight: 1,
                  padding: '2px 5px', borderRadius: 10,
                  minWidth: 16, textAlign: 'center',
                }}>
                  {unreadAlerts > 99 ? '99+' : unreadAlerts}
                </span>
              )}
            </span>
            <span className="dash-grid-label">Alerts</span>
          </button>
          {/* Row 2: Pause Devices | Recordings | Help */}
          <button className="dash-grid-btn" onClick={() => setSheet('pause')}>
            <span className={`dash-grid-icon-circle ${pauseState.paused ? 'dash-icon-orange' : 'dash-icon-blue'}`}>⏸</span>
            <span className="dash-grid-label">
              {pauseState.paused ? 'Pause Devices (Paused)' : 'Pause Devices'}
            </span>
          </button>
          {productHas('recordings') && (
            <button className="dash-grid-btn" onClick={() => navigate('/recordings')}>
              <span className="dash-grid-icon-circle dash-icon-red">📼</span>
              <span className="dash-grid-label">Recordings</span>
            </button>
          )}
          <button className="dash-grid-btn" onClick={() => setSheet('help')}>
            <span className="dash-grid-icon-circle dash-icon-gray">❓</span>
            <span className="dash-grid-label">Help</span>
          </button>
          {/* Schedule — hidden */}
          <button className="dash-grid-btn" onClick={() => navigate('/schedule')} style={{ display: 'none' }}>
            <span className="dash-grid-icon-circle dash-icon-purple">📅</span>
            <span className="dash-grid-label">Schedule</span>
          </button>
        </div>
      </div>

      {(sheet === 'play' || sheet === 'pause') && (
        <DevicesSheet
          paused={pauseState.paused}
          remaining={pauseState.remaining_secs}
          onClose={() => setSheet(null)}
          onChanged={loadPauseStatus}
          section={sheet}
        />
      )}
      {sheet === 'notifications' && <NotificationsSheet onClose={() => { setSheet(null); loadAway(); }} />}
      {sheet === 'help'          && <HelpSheet onClose={() => setSheet(null)} />}
{drawerOpen && <NavDrawer onClose={() => setDrawerOpen(false)} onLogout={handleLogout} />}

      {sosConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}
          onClick={closeSOS}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 14, padding: '24px 20px', width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
            {sosDone ? (
              sosStopped ? (
                <div style={{ fontSize: 18, fontWeight: 700, color: '#22c55e', textAlign: 'center' }}>✅ SOS Stopped</div>
              ) : (
                <>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#ef4444', textAlign: 'center' }}>🆘 Panic Sent!</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
                    Already attended? Stop it to silence the alert on every device.
                  </div>
                  <button onClick={stopSOS} disabled={sosStopping}
                    style={{
                      padding: '14px', borderRadius: 10, fontSize: 15, fontWeight: 700,
                      background: '#22c55e', color: '#fff', border: 'none',
                      cursor: sosStopping ? 'not-allowed' : 'pointer', opacity: sosStopping ? 0.7 : 1,
                    }}>
                    {sosStopping ? 'Stopping…' : '✅ Stop SOS'}
                  </button>
                  <button onClick={closeSOS}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer' }}>
                    Hide — SOS stays active
                  </button>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
                    You can stop it any time from the red SOS bar.
                  </div>
                </>
              )
            ) : (
              <>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#ef4444', textAlign: 'center' }}>🆘 SOS / Panic</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
                  Select a reason — all devices will activate and registered users will be notified.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {SOS_REASONS.map(r => (
                    <button key={r.key}
                      onClick={() => setSosReason(r.key)}
                      style={{
                        padding: '12px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600,
                        textAlign: 'left', cursor: 'pointer',
                        border: sosReason === r.key ? '2px solid #ef4444' : '1.5px solid var(--border)',
                        background: sosReason === r.key ? 'rgba(239,68,68,0.1)' : 'var(--surface-alt)',
                        color: sosReason === r.key ? '#ef4444' : 'var(--text)',
                      }}>
                      {r.label}
                    </button>
                  ))}
                  {sosReason === 'Other' && (
                    <input
                      autoFocus
                      type="text"
                      placeholder="Describe the reason…"
                      value={sosOtherText}
                      onChange={e => setSosOtherText(e.target.value)}
                      style={{
                        padding: '12px 14px', borderRadius: 10, fontSize: 14,
                        border: '1.5px solid #ef4444', background: 'var(--surface-alt)',
                        color: 'var(--text)', outline: 'none', width: '100%', boxSizing: 'border-box',
                      }}
                    />
                  )}
                </div>
                <button
                  onClick={fireSOS}
                  disabled={!sosReason || sosBusy}
                  style={{
                    padding: '14px', borderRadius: 10, fontSize: 15, fontWeight: 700,
                    background: sosReason ? '#ef4444' : 'var(--surface-alt)',
                    color: sosReason ? '#fff' : 'var(--text-muted)',
                    border: 'none', cursor: sosReason ? 'pointer' : 'not-allowed', opacity: sosBusy ? 0.7 : 1,
                  }}>
                  {sosBusy ? 'Sending…' : '🆘 Trigger Now'}
                </button>
                <button onClick={closeSOS}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {toast && <div className="pk-toast ok dash-toast">{toast}</div>}
    </div>
  );
}
